import { useCallback, useEffect, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HistoryIcon from '@mui/icons-material/History'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import Typography from '@mui/joy/Typography'
import { MobileLoadAccessPolicy, MobileLoadAccessPolicyInput, mobileLoadAccessApi, mobileLoadAccessErrorMessage } from 'api/mobileLoadAccess'
import { StatusBadge, Switch, Toast, ToastTone } from '../_ds/Primitives'
import MobileLoadAccessForm, { GROUP_LABELS } from './MobileLoadAccessForm'
import { mobilePlanDisplayName } from '@numeo/types-core'
import MobileLoadAccessHistory from './MobileLoadAccessHistory'

interface ToastState {
    open: boolean
    message: string
    tone: ToastTone
}

type Confirmation = { action: 'delete' | 'toggle'; policy: MobileLoadAccessPolicy } | null

export default function MobileLoadAccessPage() {
    const [policies, setPolicies] = useState<MobileLoadAccessPolicy[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [editing, setEditing] = useState<MobileLoadAccessPolicy | null | undefined>(undefined)
    const [history, setHistory] = useState<MobileLoadAccessPolicy | null>(null)
    const [confirmation, setConfirmation] = useState<Confirmation>(null)
    const [saving, setSaving] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)
    const [toast, setToast] = useState<ToastState>({ open: false, message: '', tone: 'success' })

    const load = useCallback(async (): Promise<void> => {
        setLoading(true)
        try {
            setPolicies(await mobileLoadAccessApi.listPolicies())
        } catch (error) {
            setToast({ open: true, message: mobileLoadAccessErrorMessage(error, 'Failed to load access rules'), tone: 'danger' })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return policies
        return policies.filter((policy) => `${policy.normalizedMc} ${policy.label ?? ''} ${policy.updatedBy}`.toLowerCase().includes(query))
    }, [policies, search])

    const save = async (input: MobileLoadAccessPolicyInput): Promise<void> => {
        setSaving(true)
        setFormError(null)
        try {
            if (editing) await mobileLoadAccessApi.updatePolicy(editing._id, input)
            else await mobileLoadAccessApi.createPolicy(input)
            setEditing(undefined)
            await load()
            setToast({ open: true, message: editing ? 'Access rule updated and active' : 'Access rule created and active', tone: 'success' })
        } catch (error) {
            setFormError(mobileLoadAccessErrorMessage(error, 'Failed to save access rule'))
        } finally {
            setSaving(false)
        }
    }

    const confirmAction = async (): Promise<void> => {
        if (!confirmation) return
        setSaving(true)
        try {
            if (confirmation.action === 'delete') {
                await mobileLoadAccessApi.deletePolicy(confirmation.policy._id)
            } else {
                const { policy } = confirmation
                await mobileLoadAccessApi.updatePolicy(policy._id, {
                    mcNumber: policy.mcNumber,
                    label: policy.label,
                    minimumPlan: policy.minimumPlan,
                    hiddenFieldGroups: policy.hiddenFieldGroups,
                    enabled: !policy.enabled,
                })
            }
            const successMessage =
                confirmation.action === 'delete' ? 'Access rule deleted' : `Access rule ${confirmation.policy.enabled ? 'disabled' : 'enabled'}`
            setConfirmation(null)
            await load()
            setToast({ open: true, message: successMessage, tone: 'success' })
        } catch (error) {
            setToast({ open: true, message: mobileLoadAccessErrorMessage(error, 'Failed to change access rule'), tone: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="mx-auto w-full max-w-7xl px-6 py-8">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Mobile load access</h1>
                    <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
                        Configure broker MC rules for Truck Loads and Load Radar. Loads stay discoverable while sensitive details are hidden until the required
                        plan.
                    </p>
                </div>
                <Button
                    startDecorator={<AddIcon />}
                    onClick={() => {
                        setFormError(null)
                        setEditing(null)
                    }}
                >
                    Add MC rule
                </Button>
            </header>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4 dark:border-slate-700">
                    <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search MC, label, or operator"
                        sx={{ minWidth: 280 }}
                    />
                    <span className="text-sm text-slate-500">
                        {filtered.length} rule{filtered.length === 1 ? '' : 's'}
                    </span>
                </div>
                {loading ? (
                    <div className="flex justify-center py-16">
                        <CircularProgress />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                        <h2 className="font-semibold text-slate-900 dark:text-slate-100">{policies.length ? 'No matching rules' : 'No MC rules yet'}</h2>
                        <p className="mt-1 text-sm text-slate-500">
                            {policies.length ? 'Try a different search.' : 'Add an MC to start controlling mobile load details.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-4 py-3">MC / label</th>
                                    <th className="px-4 py-3">Minimum plan</th>
                                    <th className="px-4 py-3">Hidden fields</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Last update</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filtered.map((policy) => (
                                    <tr key={policy._id}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900 dark:text-slate-100">MC {policy.normalizedMc}</div>
                                            <div className="text-xs text-slate-500">{policy.label || 'No label'}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge tone="info">{mobilePlanDisplayName(policy.minimumPlan)}</StatusBadge>
                                        </td>
                                        <td className="max-w-sm px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                                            {policy.hiddenFieldGroups.map((group) => GROUP_LABELS[group]).join(', ') || 'Mandatory fields only'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Switch
                                                checked={policy.enabled}
                                                onChange={() => setConfirmation({ action: 'toggle', policy })}
                                                ariaLabel={`${policy.enabled ? 'Disable' : 'Enable'} MC ${policy.normalizedMc}`}
                                            />
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                                            <div>{new Date(policy.updatedAt).toLocaleString()}</div>
                                            <div>
                                                {policy.updatedBy} · r{policy.revision}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-right">
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                aria-label={`History for MC ${policy.normalizedMc}`}
                                                onClick={() => setHistory(policy)}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                aria-label={`Edit MC ${policy.normalizedMc}`}
                                                onClick={() => {
                                                    setFormError(null)
                                                    setEditing(policy)
                                                }}
                                            >
                                                <EditOutlinedIcon />
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                aria-label={`Delete MC ${policy.normalizedMc}`}
                                                onClick={() => setConfirmation({ action: 'delete', policy })}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing !== undefined && (
                <MobileLoadAccessForm
                    key={editing?._id ?? 'new'}
                    open
                    policy={editing}
                    saving={saving}
                    error={formError}
                    onClose={() => setEditing(undefined)}
                    onSave={save}
                />
            )}
            <MobileLoadAccessHistory policy={history} onClose={() => setHistory(null)} />
            <Modal open={Boolean(confirmation)} onClose={saving ? undefined : () => setConfirmation(null)}>
                <ModalDialog>
                    <Typography level="h3">
                        {confirmation?.action === 'delete' ? 'Delete this rule?' : `${confirmation?.policy.enabled ? 'Disable' : 'Enable'} this rule?`}
                    </Typography>
                    <Typography level="body-sm" textColor="neutral.600">
                        This change takes effect immediately for MC {confirmation?.policy.normalizedMc}.
                    </Typography>
                    <div className="mt-4 flex justify-end gap-2">
                        <Button variant="plain" color="neutral" disabled={saving} onClick={() => setConfirmation(null)}>
                            Cancel
                        </Button>
                        <Button color={confirmation?.action === 'delete' ? 'danger' : 'primary'} loading={saving} onClick={() => void confirmAction()}>
                            Confirm
                        </Button>
                    </div>
                </ModalDialog>
            </Modal>
            <Toast open={toast.open} tone={toast.tone} message={toast.message} onClose={() => setToast((value) => ({ ...value, open: false }))} />
        </div>
    )
}
